import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Briefcase, CalendarClock, Trophy, GraduationCap,
} from 'lucide-react';
import WorkflowListShell from '../components/workflow/WorkflowListShell';
import EngagementProgressBoard from '../components/workflow/EngagementProgressBoard';
import { engagementListColumns } from '../components/shared/engagementListColumns';
import EngagementStatusFilter from '../components/shared/EngagementStatusFilter';
import SessionRow, { TeachingRow, DateChip, todayISO, dayAfter, fmtHours } from '../components/lnd/SessionRow';
import { AttendanceList, AbsenceList, QualificationList, CpdList } from '../components/lnd/TrainingLists';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { bandOf, BAND_BY_KEY } from '../components/lnd/complianceBands';
import { computeTrainingStats } from '../utils/trainingStats';
import { formatDate } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { canSeeAuditStages } from '../components/workflow/useWorkflowPerms';
import { sectionTitle, sectionTitleBar } from '../components/workflow/constants';

/**
 * My Records — everything the logged-in person is personally attached to
 * (docs/modules/my-records.md).
 *
 * Four tabs, one rule: you see YOUR OWN. No permission key is involved —
 * engagements are scoped by team membership and training by the staff row
 * matching your login email, both decided server-side. Read-only throughout:
 * corrections go through Management or HR, never here.
 *
 * The Overview shows what is CURRENT and links in here for the whole record,
 * including finished engagements and past sessions. Deep links carry the tab
 * (?tab=sessions) and optionally a session to highlight (&session=12).
 *
 * Every row on this page is a shared component — the Engagements tab renders
 * the SAME list shell/columns/board as Management → Engagements,
 * the training tabs share components/lnd/TrainingLists — so the
 * management screens and this page can never describe the same record two
 * different ways. What differs is scope (mine only) and capability (no
 * create/edit/delete here).
 */

// ISO day N months before `iso` (same day-of-month, clamped by Date).
const monthsAgo = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  const x = new Date(y, m - 1 - n, d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const TABS = [
  { key: 'engagements', label: 'Engagements', icon: Briefcase },
  { key: 'sessions', label: 'Training Sessions', icon: CalendarClock },
  { key: 'quals', label: 'Qualifications', icon: Trophy },
  { key: 'cpd', label: 'External CPD', icon: GraduationCap },
];

// Nobody's login is guaranteed to be a staff member (a pure management account
// isn't). Say so plainly instead of showing four empty tables.
const NotStaff = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-10 text-center">
    <p className="text-sm text-slate-600">Your login isn’t linked to an active staff record.</p>
    <p className="text-xs text-slate-500 mt-1">
      My Records is matched by email address — ask an administrator to check your staff profile.
    </p>
  </div>
);

const Panel = ({ title, count, hint = null, children }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
      <div className="flex-1 flex items-center flex-wrap gap-x-2">
        <h3 className={sectionTitle}>
          <span className={sectionTitleBar} aria-hidden="true" />
          {title}
        </h3>
        {hint && <span className="text-xs text-slate-500">{hint}</span>}
      </div>
      {count != null && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
          {count}
        </span>
      )}
    </div>
    {children}
  </section>
);

// ── Engagements tab ────────────────────────────────────────────────────────
//
// 與 Management → Engagements 同一套畫面：同一份欄位
// （engagementListColumns）、同一個清單殼（WorkflowListShell，embedded 模式）、
// 同一張看板（EngagementProgressBoard）。範圍上的差別有三件，不是
// 「長相」：資料走 /api/my/engagements（只回我在團隊上、或被點名為 EQR 的
// 案子——每列帶 myRoles，多一欄「Your role」說明它為什麼是我的）、看板帶
// scope=mine（qm:view 也不放寬——My Records means mine）、沒有 New engagement
// 鈕（這一面唯讀，建案在 Management 或走 Engagement Registration）。
// 另外依權限少幾欄：沒有 Audit 鑰匙（canSeeAuditStages）的人，List 不列
// Sign Off／Archival Deadline，Grouped 不列 Signed Financial Statements／
// QC35 Form 兩關與 Next Deadline；Management 那面一律全列。
// 列點下去 → /my/engagements/:id（唯讀詳情）。

// What the viewer is to each row — the one column Management has no use for
// (there is no "me" on the management list), so it is appended here rather
// than added to the shared column set.
// 五種角色五種顏色,一列可能掛好幾個(同時是合夥人又在名冊上)。
// 灰色不能用——右邊的 Status 與 Current stage 晶片就是灰,同色會讓人以為
// 在講同一件事;綠/琥珀/紅同理,那三色在這張表裡專講關卡狀態。
// 三個 lead 角色走同一個藍系、由深到淺照職級排,team 與 EQR 各自離開那個
// 家族:一眼就分得出「我是帶案的、我在名冊上、還是我被點名覆核」。
const ROLE_META = {
  partner: { label: 'Partner', cls: 'bg-indigo-50 text-indigo-700' },
  director: { label: 'Director', cls: 'bg-blue-50 text-blue-700' },
  manager: { label: 'Manager', cls: 'bg-sky-50 text-sky-700' },
  team: { label: 'Team', cls: 'bg-teal-50 text-teal-700' },
  eqr: { label: 'EQR', cls: 'bg-violet-50 text-violet-700' },
};
const myRoleColumn = {
  key: 'myRole',
  label: 'Your Role',
  render: (r) => (
    <span className="flex flex-wrap gap-1">
      {(r.myRoles || []).map((k) => {
        const meta = ROLE_META[k];
        return (
          <span
            key={k}
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
              meta ? meta.cls : 'bg-slate-100 text-slate-700'
            }`}
          >
            {meta ? meta.label : k}
          </span>
        );
      })}
    </span>
  ),
};
const myColumns = (showAudit) => {
  const cols = engagementListColumns({ showAudit });
  const at = cols.findIndex((c) => c.key === 'partner') + 1;
  return [...cols.slice(0, at), myRoleColumn, ...cols.slice(at)];
};

const viewToggleCls = (on) =>
  `px-3 py-1.5 text-sm font-medium whitespace-nowrap ${
    on ? 'bg-blue-50 text-blue-700' : 'bg-white text-slate-600 hover:bg-slate-50'
  }`;

const EngagementsTab = () => {
  // Signed Financial Statements / QC35 Form columns are Audit work — shown
  // only to holders of an Audit key (List: Sign Off, Archival Deadline;
  // Grouped: the two gates and Next Deadline).
  const { hasPermission } = useAuth();
  const showAudit = canSeeAuditStages(hasPermission);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'grouped' ? 'grouped' : 'list';
  const setView = (v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'grouped') next.set('view', 'grouped');
      else next.delete('view');
      return next;
    }, { replace: true });
  };
  // Status 晶片跟 Grouped 看板同一排（同一個元件、同一份選項與同一組預設），
  // 兩個檢視切來切去看到的案子才會是同一批。
  // null＝選項還沒回來；回來之前不開表，免得先閃一次未篩選的全部。
  const [statusOptions, setStatusOptions] = useState([]);
  const [statuses, setStatuses] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchJSON('/api/my/engagements/options');
        setStatusOptions(d.statuses || []);
        setStatuses(d.defaultStatuses || []);
      } catch {
        setStatuses([]);   // 拿不到選項就不篩，表照樣看得到
      }
    })();
  }, []);

  const toggleStatus = (value) => setStatuses((prev) => (
    (prev || []).includes(value) ? prev.filter((s) => s !== value) : [...(prev || []), value]
  ));

  // 殼的工具列是 gap-3（給下拉／輸入框用），晶片自己包一層 gap-2，跟看板
  // 那排一樣密。
  const statusFilter = (
    <span className="flex flex-wrap items-center gap-2">
      <EngagementStatusFilter options={statusOptions} active={statuses || []} onToggle={toggleStatus} />
    </span>
  );

  const viewToggle = (
    <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" role="group">
      {[['list', 'List'], ['grouped', 'Grouped']].map(([v, label], i) => (
        <button
          key={v}
          type="button"
          onClick={() => setView(v)}
          className={`${i > 0 ? 'border-l border-slate-300 ' : ''}${viewToggleCls(view === v)}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (view === 'grouped') {
    return (
      <EngagementProgressBoard
        scope="mine"
        engagementTo={(e) => `/my/engagements/${e.engagementId}`}
        toolbarExtra={viewToggle}
        showAudit={showAudit}
      />
    );
  }

  if (statuses === null) return <p className="text-center text-sm text-slate-500 mt-8">Loading…</p>;

  return (
    <WorkflowListShell
      embedded
      canCreate={false}
      showHideCompleted={false}
      apiPath="/api/my/engagements"
      extraParams={{ statuses: statuses.join(',') }}
      rowTo={(r) => `/my/engagements/${r.id}`}
      searchPlaceholder="Search file ref, engagement, client group or entity…"
      defaultSort={{ key: 'yearEnd', direction: 'desc' }}
      emptyText="You are not on any engagement team, or named as its EQR, yet."
      filters={
        <>
          {statusFilter}
          {viewToggle}
        </>
      }
      columns={myColumns(showAudit)}
    />
  );
};


// ── Training-year summary (Sessions tab header) ────────────────────────────

const TrainingYearSummary = ({ training }) => {
  const { period, attendance, cpdRecords } = training;
  const stats = useMemo(
    () => computeTrainingStats(attendance || [], cpdRecords || [], period.start, period.end),
    [attendance, cpdRecords, period]
  );
  const target = period.target || 0;
  const band = BAND_BY_KEY[bandOf(stats.totalMandatoryHours, target)];
  const gap = Math.max(0, target - stats.totalMandatoryHours);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm">
            <span className="text-2xl font-bold tabular-nums text-slate-900">{fmtHours(stats.totalMandatoryHours)}</span>
            <span className="text-slate-500"> / {fmtHours(target)} h mandatory</span>
            {gap > 0 && <span className="text-amber-700 font-medium"> · {fmtHours(gap)} h to go</span>}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Training year {formatDate(period.start)} – {formatDate(period.end)}
          </p>
        </div>
        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${band.badge}`}>
          {band.short}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
        <span>Non-mandatory <span className="font-medium tabular-nums">{fmtHours(stats.totalNonMandatoryHours)} h</span></span>
        <span>External CPD <span className="font-medium tabular-nums">{fmtHours(stats.totalExternalHours)} h</span></span>
        <span className="text-slate-500">Figures cover the current training year only; the lists below are all time.</span>
      </div>
    </div>
  );
};

// ── The page ───────────────────────────────────────────────────────────────

const MyRecords = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'engagements';
  const highlightSession = parseInt(searchParams.get('session'), 10) || null;

  const [training, setTraining] = useState(null);
  const [trainingError, setTrainingError] = useState(null);
  const [staffLinked, setStaffLinked] = useState(true);
  const highlightRef = useRef(null);
  // External CPD: the record being withdrawn (its declaration is deleted
  // and the hours come off), confirmed first.
  const [withdrawing, setWithdrawing] = useState(null);
  const [trainingReload, setTrainingReload] = useState(0);

  // ONE fetch feeds Sessions, Qualifications and External CPD — they are one
  // query server-side (staffJourney), so splitting it per tab would ask the
  // same question three times.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON('/api/my/training');
        if (cancelled) return;
        setTraining(data);
        if (data.staffLinked === false) setStaffLinked(false);
      } catch (e) { if (!cancelled) setTrainingError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [trainingReload]);

  // A deep link from the Overview lands on the session it was pointing at.
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [training, tab, highlightSession]);

  const selectTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    next.delete('session'); // the highlight belonged to the link, not the tab
    setSearchParams(next, { replace: true });
  };

  const today = todayISO();
  const tomorrow = dayAfter(today);
  // Every row on this tab opens the session's own page (facts, materials,
  // quiz) — docs/modules/my-records.md §6.
  const sessionTo = (s) => `/my/sessions/${s.sessionId || s.id}`;

  // Date window for the Sessions tab (owner 2026-10-05): the record grows
  // for years, so it opens on the last six months plus everything still to
  // come; both ends can be moved, and blank = no bound on that side.
  const [rangeFrom, setRangeFrom] = useState(() => monthsAgo(today, 6));
  const [rangeTo, setRangeTo] = useState('');
  const inRange = (d) => (!rangeFrom || d >= rangeFrom) && (!rangeTo || d <= rangeTo);
  const upcoming = useMemo(() => (training?.upcoming || []).filter((s) => inRange(s.date)), [training, rangeFrom, rangeTo]); // eslint-disable-line react-hooks/exhaustive-deps
  const teaching = useMemo(() => (training?.teaching || []).filter((s) => inRange(s.date)), [training, rangeFrom, rangeTo]); // eslint-disable-line react-hooks/exhaustive-deps
  const missed = useMemo(() => (training?.missed || []).filter((s) => inRange(s.date)), [training, rangeFrom, rangeTo]); // eslint-disable-line react-hooks/exhaustive-deps
  const absences = useMemo(() => (training?.absences || []).filter((s) => inRange(s.date)), [training, rangeFrom, rangeTo]); // eslint-disable-line react-hooks/exhaustive-deps
  // Newest first everywhere on this page: the reader starts from now.
  const attendanceDesc = useMemo(
    () => [...(training?.attendance || [])].filter((s) => inRange(s.date)).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
    [training, rangeFrom, rangeTo] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const totalSessions = training
    ? ['upcoming', 'teaching', 'missed', 'absences', 'attendance'].reduce((n, k) => n + (training[k] || []).length, 0)
    : 0;
  const shownSessions = upcoming.length + teaching.length + missed.length + absences.length + attendanceDesc.length;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">My Records</h1>
        <p className="text-sm text-slate-500 mt-1">
          Your engagements and training record.
        </p>
      </div>

      {/* Tabs. overflow-y-hidden is required, not decorative: setting
          overflow-x to auto implicitly promotes overflow-y from visible to
          auto, and the nav's -mb-px (which lays the active tab's underline
          over the container's border) overflows by exactly that 1px — enough
          to raise a vertical scrollbar on a page that has nothing to scroll. */}
      <div className="border-b border-slate-200 mb-5 overflow-x-auto overflow-y-hidden">
        <nav className="flex gap-1 -mb-px">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => selectTab(key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                tab === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* staffLinked 由 /api/my/training 一次判定（同一條 email→staff 規則），
          engagements tab 不自己回報。 */}
      {tab === 'engagements' && (
        staffLinked ? <EngagementsTab /> : <NotStaff />
      )}

      {tab !== 'engagements' && (
        <>
          {trainingError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              {trainingError}
            </div>
          )}
          {!trainingError && !training && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-8 text-center text-sm text-slate-500">
              Loading…
            </div>
          )}
          {training && !staffLinked && <NotStaff />}

          {training && staffLinked && tab === 'sessions' && (
            <div className="space-y-5">
              {training.period && <TrainingYearSummary training={training} />}

              {/* Date window — applies to every list below. */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <span className="font-medium text-slate-700">Show sessions</span>
                <label className="inline-flex items-center gap-2 text-slate-600">
                  from
                  <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
                </label>
                <label className="inline-flex items-center gap-2 text-slate-600">
                  to
                  <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
                </label>
                <span className="ml-auto text-xs text-slate-500">{shownSessions} of {totalSessions} sessions shown</span>
              </div>
              {/* Sessions I run. Only shown to people booked as a trainer —
                  the row says what the session still needs (materials, quiz)
                  and opens the page where the trainer does both. */}
              {teaching.length > 0 && (
                <Panel title="Teaching" count={teaching.length} hint="Sessions you run — open one to upload materials and write the quiz.">
                  <div className="divide-y divide-slate-100">
                    {teaching.map((s) => (
                      <Link key={s.id} to={sessionTo(s)} className="block hover:bg-slate-50 transition-colors">
                        <div ref={highlightSession === s.id ? highlightRef : null}>
                          <TeachingRow session={s} today={today} tomorrow={tomorrow} highlighted={highlightSession === s.id} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </Panel>
              )}
              <Panel title="Upcoming" count={upcoming.length}>
                {upcoming.length === 0 ? (
                  <p className="text-sm text-slate-500 px-5 py-6 text-center">
                    Nothing scheduled for you right now.
                  </p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {upcoming.map((s) => (
                      <Link key={s.id} to={sessionTo(s)} className="block hover:bg-slate-50 transition-colors">
                        <div ref={highlightSession === s.id ? highlightRef : null}>
                          <SessionRow
                            session={s}
                            today={today}
                            tomorrow={tomorrow}
                            highlighted={highlightSession === s.id}
                          />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </Panel>
              {/* Sessions that ended without me and without an absence on file —
                  the Overview's File Absence rule, listed here so the record
                  does not simply lose the session the moment it ends. */}
              {missed.length > 0 && (
                <Panel title="Missed" count={missed.length} hint="Not attended and no absence filed yet.">
                  <div className="divide-y divide-slate-100">
                    {missed.map((s) => (
                      <div key={s.id} className="flex items-start gap-3 px-5 py-3">
                        <DateChip date={s.date} today={today} tone="amber" />
                        <div className="min-w-0 flex-1">
                          <Link to={sessionTo(s)} className="text-sm font-medium text-slate-800 hover:text-blue-600 truncate block">{s.courseTitle}</Link>
                          <p className="mt-1 text-xs text-amber-700">Not attended — declare your absence.</p>
                        </div>
                        <Link to={s.href} className="shrink-0 px-2.5 py-1 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700">
                          File Absence
                        </Link>
                      </div>
                    ))}
                  </div>
                </Panel>
              )}
              <Panel title="Attended" count={attendanceDesc.length}>
                <AttendanceList records={attendanceDesc} today={today} rowTo={sessionTo} />
              </Panel>
              {absences.length > 0 && (
                <Panel title="Absences" count={absences.length}>
                  <AbsenceList records={absences} today={today} rowTo={sessionTo} />
                </Panel>
              )}
            </div>
          )}

          {training && staffLinked && tab === 'quals' && (
            <Panel title="Qualifications Held" count={(training.qualifications || []).length}>
              <QualificationList records={training.qualifications || []} />
            </Panel>
          )}

          {training && staffLinked && tab === 'cpd' && (
            <Panel title="External CPD" count={(training.cpdRecords || []).length}>
              <CpdList records={training.cpdRecords || []} onWithdraw={setWithdrawing} />
              {withdrawing && (
                <ConfirmDeleteModal
                  title={`Withdraw ${withdrawing.requestNo || 'declaration'}`}
                  message="This deletes the declaration and the External CPD record it filed — the hours come off your training record. This cannot be undone."
                  onClose={() => setWithdrawing(null)}
                  onConfirm={async () => {
                    await fetchJSON(`/api/ld/cpd-requests/${withdrawing.requestId}`, { method: 'DELETE' });
                    setWithdrawing(null);
                    setTrainingReload((n) => n + 1);
                  }}
                />
              )}
            </Panel>
          )}
        </>
      )}
    </div>
  );
};

export default MyRecords;
